import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { type CountryCode, type ProjectCode } from '@constants'
import { useFastpassCampaignGoogleAnalyticEvent } from '@dni-analysis'
import { useApiSearchCampaignProduct } from '@hooks-api/fortune-fastpass/useApiSearchCampaignProduct'
import { type Variation } from '@hooks-api/types/dataModel/products'
import { Drawer } from '@pets-ui/Drawer/Drawer'
import { IconButton } from '@pets-ui/IconButton/IconButton'
import { Search } from '@pets-ui/icons'
import { useEffect, useState } from 'react'
import Header from '../Header/Header'
import Loading from '../Loading/Loading'
import SearcherNoResultsDisplay from '../SearcherNoResults/SearcherNoResultsDisplay'
import SearcherSearchResultsDisplay from '../SearcherSearchResultsDisplay/SearcherSearchResultsDisplay'
import SearchHistoryCache from '../util/search-history-cache'
import style from './Searcher.module.scss'
import SearcherPopKeywordDisplay from './SearcherPopKeywordDisplay'
import { SearcherRecentKeywordDisplay } from './SearcherRecentKeywordDisplay'
import { UserInputValidator } from './user-input-validator'
const inputValidator = new UserInputValidator()

interface SearcherProps {
  onDrawerToggle: () => void
  isDrawerOpen: boolean
  projectCode: ProjectCode
  campaignId: number
  countryCode: CountryCode
  onSearch?: (keyword: string) => void
  onResultClick?: (product: Variation) => void
  onPastResultClick?: (past_keyword: string) => void
  environment?: 'campaign' | 'merchant'
}

const cacheInstanceMap = new Map<string, SearchHistoryCache>()
const getCacheInstance = (project: string): SearchHistoryCache | undefined => {
  project = project.toLowerCase()
  if (cacheInstanceMap.has(project)) return cacheInstanceMap.get(project)
  else {
    // if every brand's search history has to be kept in different caches, use const cacheName = project + 'SearchKeys';
    const cacheName = project + 'SearchKeys'
    cacheInstanceMap.set(project, new SearchHistoryCache(cacheName, 30))
    return cacheInstanceMap.get(project)
  }
}
export const Searcher = ({ isDrawerOpen, onDrawerToggle, projectCode, campaignId, onSearch, onResultClick, onPastResultClick, countryCode, environment }: SearcherProps) => {
  // used by search caches
  // inputs before enter clicked
  const [userInput, setUserInput] = useState<string>('')
  // search results from rest api
  const [searchResults, setSearchResults] = useState<Variation[]>([])
  // pageNum specified in search
  const [fetchPageNum, setFetchPageNum] = useState<number>(-1)
  // tryFetchPage is used to try to fetch the next page
  const [tryFetchPageNum, setTryFetchPageNum] = useState<number>(-1)
  // INIT_MODE: display popular keywords and keyword history. SEARCH_MODE: display info related to search
  const [workMode, setWorkMode] = useState<string>('INIT_MODE')
  // userInput that is verified to be valid, so sent to search backend and saved in cache
  const [verifiedUserInput, setVerifiedUserInput] = useState<string>('')
  // used to show keywords in search history
  const [cachedKeywords, setCachedKeywords] = useState<string[]>([])
  // specify whether user search request is triggered
  const [isSearchRequestedByUser, setIsSearchRequestedByUser] = useState<boolean>(false)

  const { sendSearchKeywordEvent } = useFastpassCampaignGoogleAnalyticEvent({ projectCode, countryCode, campaignId })
  // this function is the entry point for search icon button click, enter key click search, hot key click search, history key click search
  const userInputSearchHandler = (userInput: string) => {
    const verifiedUserInput = inputValidator.validate(userInput)
    if (verifiedUserInput === '') {
      // empty user input, do nothing
    }
    else {
      const keywords: string[]
        // FIXME: should fix addFilteredKeywordHandler typing
        = getCacheInstance(projectCode)?.addFilteredKeywordHandler(verifiedUserInput) || []
      if (environment === 'campaign') {
        sendSearchKeywordEvent({ searchKeyword: verifiedUserInput })
      }
      getCacheInstance(projectCode)?.writeToLocalStorage(keywords === null ? [] : keywords)
      setVerifiedUserInput(verifiedUserInput)
      setWorkMode('LOAD_MODE')
      setTryFetchPageNum(0)
      setFetchPageNum(-1)
      setIsSearchRequestedByUser(true)
    }
  }
  const resetSearch = () => {
    setTryFetchPageNum(-1)
    setFetchPageNum(-1)
    setUserInput('')
    setSearchResults([])
    setWorkMode('INIT_MODE')
    setIsSearchRequestedByUser(false)
  }
  const { campaignCodeFromUrl } = useCampaignCode()
  const { trigger: searchProduct } = useApiSearchCampaignProduct()

  const search = async () => {
    if (!campaignCodeFromUrl) return
    const { data: results } = await searchProduct({
      code: campaignCodeFromUrl,
      page: String(tryFetchPageNum),
      per_page: String(20),
      query: verifiedUserInput,
    })
    if (results != null) {
      /**
       * 切品牌功能導向產品的功能寫好之後，記得要拿掉 sameBrandData，不用 filter 了
       */
      const sameBrandData = results.data.filter(item => item.project_code === projectCode)
      // config results
      if (tryFetchPageNum === 0) {
        setSearchResults(sameBrandData)
      }
      else {
        setSearchResults([...searchResults, ...sameBrandData])
      }
      // config pageNum
      if (sameBrandData.length === 0) {
        // tryFetch the next page and find nothing can be fetched => already fetch all products
        setTryFetchPageNum(fetchPageNum)
      }
      else {
        // prepare for next fetching
        setFetchPageNum(tryFetchPageNum)
      }
    }
    else {
      // tryFetch the next page and find nothing can be fetched => already fetch all products
      setTryFetchPageNum(fetchPageNum)
    }
    // user gets control to ui
    setWorkMode('SEARCH_MODE')
    setIsSearchRequestedByUser(false)
    setUserInput(verifiedUserInput)
  }

  useEffect(() => {
    // search requests are triggered by the user, or infinite scrolling

    if (
      (verifiedUserInput.length > 0 && isSearchRequestedByUser)
      || tryFetchPageNum !== fetchPageNum
    ) {
      search()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchRequestedByUser, tryFetchPageNum])

  useEffect(() => {
    if (!isDrawerOpen) {
      resetSearch()
    }
    else {
      const keywords = getCacheInstance(projectCode)?.readLocalStorageToCache() || null
      setCachedKeywords(keywords === null ? [] : keywords)
    }
  }, [isDrawerOpen, projectCode])

  const searchKeywordInputComponent = (
    <input
      type="text"
      placeholder="輸入您想找的商品"
      className={style.input}
      value={userInput}
      onChange={(e) => {
        setUserInput(e.target.value)
      }}
      onKeyUp={(e) => {
        if (userInput === '') return
        if (e.key === 'Enter') {
          userInputSearchHandler(userInput)
          onSearch?.(userInput)
        }
      }}
    />
  )
  const searchButtonComponent = (
    <IconButton
      onClick={() => {
        const key = inputValidator.validate(userInput)
        if (key === '') return
        setVerifiedUserInput(key)
        userInputSearchHandler(key)
        onSearch?.(key)
      }}
    >
      <Search />
    </IconButton>
  )
  let searchResultsDisplayComponent = <div />
  if (workMode === 'LOAD_MODE') {
    searchResultsDisplayComponent = <Loading />
  }
  else if (workMode === 'SEARCH_MODE') {
    if (searchResults.length > 0) {
      searchResultsDisplayComponent = (
        <SearcherSearchResultsDisplay
          onToggle={() => {
            onDrawerToggle()
            setWorkMode('INIT_MODE')
          }}
          products={searchResults}
          tryFetchNextPage={() => {
            setTryFetchPageNum(fetchPageNum + 1)
          }}
          keyword={verifiedUserInput}
          projectCode={projectCode}
          campaignId={campaignId}
          countryCode={countryCode}
          environment={environment}
          onResultClick={onResultClick}
        />
      )
    }
    else {
      searchResultsDisplayComponent = (
        <SearcherNoResultsDisplay
          onCloseParent={() => {
            onDrawerToggle()
            setWorkMode('INIT_MODE')
          }}
          keyword={verifiedUserInput}
        />
      )
    }
  }

  const searchRecentKeywordDisplayComponent
    = cachedKeywords.length === 0 || workMode === 'SEARCH_MODE'
      ? (
        <div />
      )
      : (
        <SearcherRecentKeywordDisplay
          searchKeywords={cachedKeywords}
          setUserInputFieldTextAndSearch={(word: string) => {
            const key = inputValidator.validate(word)
            setVerifiedUserInput(key)
            userInputSearchHandler(key)
          }}
          clearSearchHistory={() => {
            getCacheInstance(projectCode)?.clearCache()
            setCachedKeywords([])
          }}
          removeSearchKey={(keyword: string) => {
            const s: string[]
            = getCacheInstance(projectCode)?.removeSearchKeywordHandler(keyword) || []
            getCacheInstance(projectCode)?.writeToLocalStorage(s === null ? [] : s)
            setCachedKeywords(s === null ? [] : s)
          }}
          onPastResultClick={onPastResultClick}
          projectCode={projectCode}
          campaignId={campaignId}
          countryCode={countryCode}
          environment={environment}
        />
      )
  const searchPopKeywordDisplayComponent
    = workMode === 'SEARCH_MODE'
      ? (
        <div />
      )
      : (
        <SearcherPopKeywordDisplay
          setUserInputFieldTextAndSearch={(word: string) => {
            const key = inputValidator.validate(word)
            setVerifiedUserInput(key)
            userInputSearchHandler(key)
          }}
        />
      )

  return (
    <div className={style.wrapper}>
      <Drawer direction="top" open={isDrawerOpen} onClose={onDrawerToggle}>
        <Header
          left={searchKeywordInputComponent}
          right={searchButtonComponent}
          classes={style.header}
        />
        <div className={style.container}>
          {searchRecentKeywordDisplayComponent}
          {searchResultsDisplayComponent}
          {searchPopKeywordDisplayComponent}
        </div>
      </Drawer>
    </div>
  )
}
